import { memo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Grid, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { OVERRIDE_AT_DIFFERENT_LEVEL } from "../../../../consts/strings";
import {
  updateModalEditOverride,
  addModalDeleteOverride,
} from "../../../../reducers/matrixProfile";
import { selectedPostcodesLevelSelector } from "../../../../reducers/ownApp/selectors";
import { ConfirmDialog } from "../../../ConfirmDialog";
import { PostcodeLine } from "../Common/PostcodeLine";
import { SingleOverride } from "../Common/SingleOverride";
import { WarningIcon } from "../../../Icons";

const useStyles = makeStyles({
  tooltip: {
    width: "300px",
    height: "28px",
    "& .MuiTooltip-tooltip": {
      textAlign: "center",
      fontSize: "14px",
      fontWeight: 400,
    },
  },
});

export const ViewOverridesContent = memo(
  ({ overrides, onValidated, onEdit }) => {
    const styles = useStyles();
    const level = useSelector(selectedPostcodesLevelSelector);
    const levelKey = level.toLowerCase();
    const [toBeDeletedOverride, setToBeDeletedOverride] = useState();
    const [showDeleteModal, setShowDeleteModal] = useState();
    const dispatch = useDispatch();

    const onDeletePress = (override) => {
      setShowDeleteModal(true);
      setToBeDeletedOverride(override);
    };

    const onDeleteConfirmed = useCallback(() => {
      setShowDeleteModal(false);
      onValidated({
        overrideUuid: toBeDeletedOverride.overrideUuid,
        isValid: true,
      });
      onEdit();
      dispatch(addModalDeleteOverride(toBeDeletedOverride));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, onValidated, toBeDeletedOverride]);

    const onDeleteDeclined = useCallback(() => setShowDeleteModal(false), []);

    const onChange = useCallback(
      (override) => {
        onEdit();
        dispatch(updateModalEditOverride(override));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dispatch]
    );

    const overridesArray = Object.values(overrides || {});

    if (!overridesArray.length) {
      return "No overrides";
    }

    return (
      <>
        {overridesArray
          .sort((a, b) => a[levelKey].localeCompare(b[levelKey]))
          .map((override, index) => {
            const disabled = override.level !== level;
            return (
              <Tooltip
                PopperProps={{
                  className: clsx("MuiTooltip-popper", styles.tooltip),
                }}
                placement={"top"}
                disableHoverListener={!disabled}
                disableTouchListener={!disabled}
                disableFocusListener={!disabled}
                enterDelay={500}
                enterNextDelay={500}
                title={OVERRIDE_AT_DIFFERENT_LEVEL}
              >
                <Grid
                  container
                  direction={"column"}
                  key={override.overrideUuid}
                  spacing={2}
                >
                  <Grid item>
                    <PostcodeLine
                      lineNo={index + 1}
                      postcode={
                        override.postcode || override.sector || override.outer
                      }
                      deleteDisabled={disabled}
                      onDelete={() => onDeletePress(override)}
                    />
                  </Grid>
                  <Grid item>
                    <SingleOverride
                      override={override}
                      disabled={disabled}
                      onChange={onChange}
                      onValidated={override.level === level && onValidated}
                    />
                  </Grid>
                  <Grid item>
                    <Divider />
                  </Grid>
                </Grid>
              </Tooltip>
            );
          })}
        <ConfirmDialog
          open={!!showDeleteModal}
          onDecline={onDeleteDeclined}
          onConfirm={onDeleteConfirmed}
        >
          <Grid container>
            <Grid item xs={2}>
              <WarningIcon />
            </Grid>
            <Grid item xs={10}>
              Are You Sure You Wish To Remove This Override?
            </Grid>
          </Grid>
        </ConfirmDialog>
      </>
    );
  }
);
